import { CommonModule } from '@angular/common';
import { Component, computed, Inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as Diff from 'diff';
import { PublishedSnapshot, TemplateVariable } from '../../../_interfaces/email.interface';
import { DateFnsModule } from 'ngx-date-fns';
import { EmailTemplatePreviewComponent } from '../email-template-preview/email-template-preview.component';
import { sortLanguageCodes } from '../../../_utils/utils';
import { getLanguageName } from '../../../_datas/languages.data';

export interface EmailDiffDialogData {
  templateName: string;
  draft: {
    htmlPart: { [langCode: string]: string };
    subjectPart: { [langCode: string]: string };
    templateVariables: TemplateVariable[];
    description: string;
  };
  published: PublishedSnapshot;
  languageCodes: string[];
  defaultLangCode: string;
}

interface DiffLine {
  type: 'added' | 'removed' | 'unchanged';
  content: string;
  lineNumber?: number;
}

@Component({
  selector: 'app-email-diff-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatTabsModule,
    DateFnsModule,
  ],
  templateUrl: './email-diff-dialog.component.html',
  styleUrl: './email-diff-dialog.component.scss'
})
export class EmailDiffDialogComponent {

  sortedLanguageCodes = sortLanguageCodes(this.data.languageCodes);
  selectedLang = signal<string>(this.data.defaultLangCode);
  selectedSection = signal<'html' | 'subject' | 'variables' | 'description'>('html');

  // Computed diffs for current language
  htmlDiff = computed(() => this.computeLineDiff(
    this.data.published.htmlPart[this.selectedLang()] || '',
    this.data.draft.htmlPart[this.selectedLang()] || ''
  ));

  subjectDiff = computed(() => this.computeWordDiff(
    this.data.published.subjectPart[this.selectedLang()] || '',
    this.data.draft.subjectPart[this.selectedLang()] || ''
  ));

  descriptionDiff = computed(() => this.computeWordDiff(
    this.data.published.description || '',
    this.data.draft.description || ''
  ));

  // Check if language has changes
  languageHasChanges = computed(() => {
    const result: { [lang: string]: boolean } = {};
    for (const lang of this.data.languageCodes) {
      const htmlChanged = this.data.published.htmlPart[lang] !== this.data.draft.htmlPart[lang];
      const subjectChanged = this.data.published.subjectPart[lang] !== this.data.draft.subjectPart[lang];
      result[lang] = htmlChanged || subjectChanged;
    }
    return result;
  });

  // Overall change summary
  changeSummary = computed(() => {
    let addedLines = 0;
    let removedLines = 0;

    for (const lang of this.data.languageCodes) {
      const diff = Diff.diffLines(
        this.data.published.htmlPart[lang] || '',
        this.data.draft.htmlPart[lang] || ''
      );
      for (const part of diff) {
        if (part.added) addedLines += part.count || 0;
        if (part.removed) removedLines += part.count || 0;
      }
    }

    return { addedLines, removedLines };
  });

  constructor(
    public dialogRef: MatDialogRef<EmailDiffDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EmailDiffDialogData,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog
  ) {}

  /**
   * Compute line-by-line diff for HTML content
   */
  private computeLineDiff(oldText: string, newText: string): DiffLine[] {
    const diff = Diff.diffLines(oldText, newText);
    const lines: DiffLine[] = [];
    let lineNum = 1;

    for (const part of diff) {
      const partLines = part.value.split('\n').filter(l => l !== '' || part.value === '\n');

      for (const line of partLines) {
        if (part.added) {
          lines.push({ type: 'added', content: line, lineNumber: lineNum++ });
        } else if (part.removed) {
          lines.push({ type: 'removed', content: line });
        } else {
          lines.push({ type: 'unchanged', content: line, lineNumber: lineNum++ });
        }
      }
    }

    return lines;
  }

  /**
   * Compute word-level diff for subjects and descriptions
   */
  private computeWordDiff(oldText: string, newText: string): SafeHtml {
    const diff = Diff.diffWords(oldText, newText);
    let html = '';

    for (const part of diff) {
      const escapedValue = this.escapeHtml(part.value);
      if (part.added) {
        html += `<span class="diff-added">${escapedValue}</span>`;
      } else if (part.removed) {
        html += `<span class="diff-removed">${escapedValue}</span>`;
      } else {
        html += escapedValue;
      }
    }

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Escape HTML for safe display
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Recursively sort object keys alphabetically for consistent comparison and display
   */
  private sortObjectKeys(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj.map(item => this.sortObjectKeys(item));
    if (typeof obj !== 'object') return obj;

    const sorted: any = {};
    Object.keys(obj).sort().forEach(key => {
      sorted[key] = this.sortObjectKeys(obj[key]);
    });
    return sorted;
  }

  /**
   * Compare two values for equality (with sorted keys for objects)
   */
  private isEqual(a: any, b: any): boolean {
    return JSON.stringify(this.sortObjectKeys(a)) === JSON.stringify(this.sortObjectKeys(b));
  }

  /**
   * Format a value as pretty-printed JSON with sorted keys
   */
  formatValue(value: any): string {
    if (value === null || value === undefined) return '';
    const sorted = this.sortObjectKeys(value);
    return JSON.stringify(sorted, null, 2);
  }

  /**
   * Check if variables have changed
   */
  hasVariableChanges(): boolean {
    const pubVars = [...this.data.published.templateVariables].sort((a, b) => a.name.localeCompare(b.name));
    const draftVars = [...this.data.draft.templateVariables].sort((a, b) => a.name.localeCompare(b.name));
    return !this.isEqual(pubVars, draftVars);
  }

  /**
   * Get variable comparison data
   */
  getVariableComparison(): { name: string; status: 'added' | 'removed' | 'changed' | 'unchanged'; published?: any; draft?: any }[] {
    const result: any[] = [];
    const pubMap = new Map(this.data.published.templateVariables.map(v => [v.name, v]));
    const draftMap = new Map(this.data.draft.templateVariables.map(v => [v.name, v]));

    // Check published variables
    for (const [name, pubVar] of pubMap) {
      const draftVar = draftMap.get(name);
      if (!draftVar) {
        result.push({ name, status: 'removed', published: pubVar });
      } else if (!this.isEqual(pubVar.value, draftVar.value)) {
        result.push({ name, status: 'changed', published: pubVar, draft: draftVar });
      } else {
        result.push({ name, status: 'unchanged', published: pubVar, draft: draftVar });
      }
    }

    // Check for new variables in draft
    for (const [name, draftVar] of draftMap) {
      if (!pubMap.has(name)) {
        result.push({ name, status: 'added', draft: draftVar });
      }
    }

    return result.sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Select language tab
   */
  selectLanguage(lang: string): void {
    this.selectedLang.set(lang);
  }

  /**
   * View published version - opens preview modal on top of this dialog
   */
  viewPublished(): void {
    const previewLangCode = this.selectedLang();
    const publishedSnapshot = this.data.published;

    const allLanguageCodes = sortLanguageCodes(this.data.languageCodes);
    const languages = allLanguageCodes.map((lc: string) => ({
      languageCode: lc,
      languageName: getLanguageName(lc),
      isValid: !!publishedSnapshot.htmlPart?.[lc]?.trim(),
      hasContent: !!publishedSnapshot.htmlPart?.[lc]?.trim()
    }));

    this.dialog.open(EmailTemplatePreviewComponent, {
      width: '100%',
      maxWidth: '900px',
      height: '100%',
      maxHeight: '90vh',
      panelClass: 'email-preview-dialog-panel',
      data: {
        templateName: this.data.templateName,
        htmlPart: publishedSnapshot.htmlPart,
        subjectPart: publishedSnapshot.subjectPart,
        templateVariables: publishedSnapshot.templateVariables,
        defaultLangCode: previewLangCode,
        languageCodes: allLanguageCodes,
        languages,
        defaultEmail: '',
        isPublishedVersion: true,
        publishedAt: publishedSnapshot.publishedAt,
        // Always use AWS SES template for published version
        source: 'published' as const,
      }
    });
  }

  /**
   * View draft version - opens preview modal on top of this dialog
   */
  viewDraft(): void {
    const previewLangCode = this.selectedLang();

    const allLanguageCodes = sortLanguageCodes(this.data.languageCodes);
    const languages = allLanguageCodes.map((lc: string) => ({
      languageCode: lc,
      languageName: getLanguageName(lc),
      isValid: !!this.data.draft.htmlPart?.[lc]?.trim(),
      hasContent: !!this.data.draft.htmlPart?.[lc]?.trim()
    }));

    this.dialog.open(EmailTemplatePreviewComponent, {
      width: '100%',
      maxWidth: '900px',
      height: '100%',
      maxHeight: '90vh',
      panelClass: 'email-preview-dialog-panel',
      data: {
        templateName: this.data.templateName,
        htmlPart: this.data.draft.htmlPart,
        subjectPart: this.data.draft.subjectPart,
        templateVariables: this.data.draft.templateVariables,
        defaultLangCode: previewLangCode,
        languageCodes: allLanguageCodes,
        languages,
        defaultEmail: '',
        isPublishedVersion: false,
        // Use raw HTML content for draft version
        source: 'editor' as const,
      }
    });
  }

  /**
   * Close dialog without action
   */
  close(): void {
    this.dialogRef.close();
  }

  /**
   * Close dialog with revert action
   */
  revertToPublished(): void {
    this.dialogRef.close({ action: 'revert' });
  }
}
